import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { MessageCircle, Smartphone } from "lucide-react";

// LDF: WhatsApp sendet immer aus dem Konto, das auf DIESEM Gerät angemeldet ist – das kann eine Webseite nicht steuern.
// Deshalb: pro Gerät einmal bestätigen lassen, wessen WhatsApp hier angemeldet ist. Sonst QR-Code zum Scannen mit dem eigenen Handy.
const KEY = "ldf.whatsapp-device";

// Auf dem Handy gibt es nur das eigene WhatsApp – dort keine Rückfrage (Tablet/Computer können verknüpfte Fremdkonten haben)
const isPhone = () => typeof navigator !== "undefined" && /iPhone|iPod|Android.+Mobile|Windows Phone/i.test(navigator.userAgent);

type Binding = { salesId: string; mine: boolean };

const readBinding = (): Binding | null => {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "null") as Binding | null;
  } catch {
    return null;
  }
};
const writeBinding = (value: Binding | null) => {
  try {
    if (value) localStorage.setItem(KEY, JSON.stringify(value));
    else localStorage.removeItem(KEY);
  } catch {
    // Speicher nicht verfügbar
  }
};

export const WhatsAppSend = ({
  href,
  phone,
  senderName,
  salesId,
}: {
  href: string;
  phone: string;
  senderName: string;
  salesId: string | number | undefined;
}) => {
  const [binding, setBinding] = useState<Binding | null>(readBinding);
  const [showQr, setShowQr] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const phoneDevice = isPhone();
  const mine = phoneDevice || (!!binding && binding.mine && binding.salesId === String(salesId));

  useEffect(() => {
    if (!showQr) return;
    QRCode.toDataURL(href, { width: 240, margin: 1, errorCorrectionLevel: "M" })
      .then(setQr)
      .catch(() => setQr(null));
  }, [showQr, href]);

  const confirmMine = () => {
    const value = { salesId: String(salesId), mine: true };
    writeBinding(value);
    setBinding(value);
  };
  const reset = () => {
    writeBinding(null);
    setBinding(null);
    setShowQr(false);
  };

  if (mine && !showQr) {
    return (
      <div className="space-y-1">
        <p className="rounded-md bg-muted px-3 py-2 text-xs">
          <strong>In WhatsApp kurz warten,</strong> bis über dem Text das Bild mit dem Briefumschlag erscheint – erst dann auf Senden tippen. Sonst kommt der Link ohne Vorschau an.
        </p>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-12 items-center justify-center gap-2 rounded-lg bg-[#25A244] px-4 font-medium text-white no-underline hover:bg-[#1f8f3b]"
        >
          <MessageCircle className="h-5 w-5" /> Aus deinem WhatsApp an {phone} senden
        </a>
        {!phoneDevice && (
        <p className="text-xs text-muted-foreground">
          Dieses Gerät ist als WhatsApp von {senderName} bestätigt.{" "}
          <button type="button" onClick={reset} className="underline cursor-pointer">
            Nicht mehr richtig?
          </button>{" "}
          ·{" "}
          <button type="button" onClick={() => setShowQr(true)} className="underline cursor-pointer">
            Lieber mit dem Handy
          </button>
        </p>
        )}
      </div>
    );
  }

  if (showQr) {
    return (
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border p-3">
        {qr ? <img src={qr} alt="QR-Code für WhatsApp" className="h-40 w-40 rounded bg-white p-1" /> : <div className="h-40 w-40 rounded bg-muted" />}
        <div className="min-w-0 flex-1 space-y-2 text-sm">
          <p className="flex items-center gap-2 font-medium">
            <Smartphone className="h-4 w-4" /> Mit deinem eigenen Handy scannen
          </p>
          <p className="text-muted-foreground">
            Kamera auf den Code halten – WhatsApp öffnet sich auf deinem Handy mit fertigem Text an {phone}. So geht der Gruß sicher aus deinem Konto raus.
            Dort kurz warten, bis das Bild mit dem Briefumschlag erscheint, dann senden.
          </p>
          {!mine && (
            <button type="button" onClick={() => setShowQr(false)} className="text-xs underline cursor-pointer">
              Zurück
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50/70 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/40">
      <p className="font-medium">Aus welchem WhatsApp soll gesendet werden?</p>
      <p className="text-muted-foreground">
        WhatsApp sendet immer aus dem Konto, das auf diesem Gerät angemeldet ist. Ist das hier dein WhatsApp, {senderName}?
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={confirmMine}
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#25A244] px-3 font-medium text-white cursor-pointer hover:bg-[#1f8f3b]"
        >
          <MessageCircle className="h-4 w-4" /> Ja, mein WhatsApp
        </button>
        <button
          type="button"
          onClick={() => setShowQr(true)}
          className="inline-flex h-10 items-center gap-2 rounded-lg border border-border bg-background px-3 cursor-pointer hover:bg-muted"
        >
          <Smartphone className="h-4 w-4" /> Nein / nicht sicher – mit meinem Handy scannen
        </button>
      </div>
    </div>
  );
};
